import { useCallback, useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import axios from "../utils/axios";
import Navbar from "../components/Navbar";
import CollapsibleSection from "../components/CollapsibleSection";
import Spinner from "../components/Spinner";
import ReactionRoles from "../components/ReactionRoles";
import UserMonitoring from "../components/UserMonitoring";
import GuildModules from "../components/GuildModules";
import LiveNotifications from "../components/LiveNotifications";
import YouTubeLiveNotifications from "../components/YouTubeLiveNotifications";
import DropNotifications from "../components/DropNotifications";
import "./ServerDetails.css";

interface User {
  id: string;
  username: string;
  discriminator: string;
  avatar: string | null;
  isAdmin?: boolean;
}

interface ServerDetailsProps {
  user: User;
}

interface Channel {
  id: string;
  name: string;
  type: number;
  parentId?: string | null;
  categoryName?: string | null;
}

interface Role {
  id: string;
  name: string;
  color: string;
  position: number;
  memberCount: number;
  permissions: string[];
}

interface Emoji {
  id: string;
  name: string;
  animated: boolean;
  url: string;
}

interface Sticker {
  id: string;
  name: string;
  description: string;
  tags: string;
  format: number;
  url: string;
}

interface GuildModuleState {
  module_id: string;
  enabled: boolean;
}

interface ServerData {
  id: string;
  name: string;
  icon: string | null;
  memberCount: number;
  channels: {
    text?: Channel[];
    voice?: Channel[];
    announcement?: Channel[];
    forum?: Channel[];
    category?: Channel[];
    other?: Channel[];
  };
  roles?: Role[];
  emojis?: Emoji[];
  stickers?: Sticker[];
}

function ServerDetails({ user }: ServerDetailsProps) {
  const { guildId } = useParams<{ guildId: string }>();
  const navigate = useNavigate();
  const [server, setServer] = useState<ServerData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [copiedId, setCopiedId] = useState<string | null>(null);
  const [showLeaveConfirm, setShowLeaveConfirm] = useState(false);
  const [leaving, setLeaving] = useState(false);
  const [activeTab, setActiveTab] = useState<"info" | "functionality">(
    "functionality",
  );
  const [liveModuleEnabled, setLiveModuleEnabled] = useState<boolean | null>(
    null,
  );
  const [youtubeLiveModuleEnabled, setYouTubeLiveModuleEnabled] = useState<
    boolean | null
  >(null);
  const [dropModuleEnabled, setDropModuleEnabled] = useState<boolean | null>(
    null,
  );

  useEffect(() => {
    axios
      .get(`/api/server/${guildId}`)
      .then((response) => {
        setServer(response.data);
      })
      .catch((err) => {
        console.error("Error fetching server:", err);
        setError("Failed to load server details");
      })
      .finally(() => {
        setLoading(false);
      });
  }, [guildId]);

  useEffect(() => {
    setLiveModuleEnabled(null);
    setYouTubeLiveModuleEnabled(null);
    setDropModuleEnabled(null);
  }, [guildId]);

  const handleModulesLoaded = useCallback((modules: GuildModuleState[]) => {
    const liveModule = modules.find((module) => module.module_id === "live");
    setLiveModuleEnabled(Boolean(liveModule?.enabled));
    const youtubeModule = modules.find(
      (module) => module.module_id === "youtube-live",
    );
    setYouTubeLiveModuleEnabled(Boolean(youtubeModule?.enabled));
    const dropModule = modules.find((module) => module.module_id === "drops");
    setDropModuleEnabled(Boolean(dropModule?.enabled));
  }, []);

  const copyToClipboard = (id: string) => {
    navigator.clipboard
      .writeText(id)
      .then(() => {
        setCopiedId(id);
        setTimeout(() => setCopiedId(null), 2000);
      })
      .catch((err) => {
        console.error("Failed to copy:", err);
      });
  };

  const handleLeaveServer = async () => {
    if (!guildId) return;

    setLeaving(true);
    try {
      await axios.post(`/api/server/${guildId}/leave`);
      // Redirect to dashboard after leaving
      navigate("/dashboard");
    } catch (error) {
      console.error("Error leaving server:", error);
      alert("Failed to leave server. Please try again.");
      setLeaving(false);
      setShowLeaveConfirm(false);
    }
  };

  const getServerIcon = () => {
    if (server?.icon) {
      return `https://cdn.discordapp.com/icons/${server.id}/${server.icon}.png`;
    }
    return null;
  };

  const channelTypeIcon = (type: number) => {
    switch (type) {
      case 0:
        return "💬";
      case 2:
        return "🔊";
      case 4:
        return "📁";
      case 5:
        return "📢";
      case 15:
        return "💭";
      default:
        return "📝";
    }
  };

  if (loading) {
    return (
      <div className="server-details">
        <Navbar user={user} />
        <div className="container">
          <Spinner size="large" text="Loading server..." />
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="server-details">
        <Navbar user={user} />
        <div className="container">
          <div className="loading-container">
            <Spinner />
            <p>Loading server details...</p>
          </div>
        </div>
      </div>
    );
  }

  if (error || !server || !server.channels) {
    return (
      <div className="server-details">
        <Navbar user={user} />
        <div className="container">
          <p className="error">
            {error || "Server not found or incomplete data"}
          </p>
          <Link to="/dashboard">← Back to Dashboard</Link>
        </div>
      </div>
    );
  }

  const totalChannels = Object.values(server.channels || {}).reduce(
    (sum, arr) => sum + (arr?.length || 0),
    0,
  );

  const liveChannels = [
    ...(server.channels.text || []),
    ...(server.channels.announcement || []),
  ];

  return (
    <div className="server-details">
      <Navbar user={user} />

      <div className="container">
        <Link to="/dashboard" className="back-button">
          ← Back to Dashboard
        </Link>
        <div className="card">
          <div className="server-header">
            {getServerIcon() ? (
              <img
                src={getServerIcon()!}
                alt={server.name}
                className="server-icon"
              />
            ) : (
              <div className="server-icon-placeholder">
                {server.name.charAt(0)}
              </div>
            )}
            <div className="server-info">
              <h1>{server.name}</h1>
              <p>👥 {server.memberCount} members</p>
              <p>📝 Server ID: {server.id}</p>
            </div>
            <button
              className="btn-leave-server"
              onClick={() => setShowLeaveConfirm(true)}
              title="Remove bot from this server"
            >
              🚪 Leave Server
            </button>
          </div>

          <div className="stats-grid">
            <div className="stat-box">
              <h3>Total Channels</h3>
              <div className="value">{totalChannels}</div>
            </div>
            <div className="stat-box">
              <h3>Text Channels</h3>
              <div className="value">{server.channels.text?.length || 0}</div>
            </div>
            <div className="stat-box">
              <h3>Voice Channels</h3>
              <div className="value">{server.channels.voice?.length || 0}</div>
            </div>
            <div className="stat-box">
              <h3>Roles</h3>
              <div className="value">{server.roles?.length || 0}</div>
            </div>
            <div className="stat-box">
              <h3>Emojis</h3>
              <div className="value">{server.emojis?.length || 0}</div>
            </div>
            <div className="stat-box">
              <h3>Stickers</h3>
              <div className="value">{server.stickers?.length || 0}</div>
            </div>
          </div>

          <div className="server-tabs">
            <div className="tab-buttons">
              <button
                type="button"
                className={`tab-button ${activeTab === "functionality" ? "active" : ""}`}
                onClick={() => setActiveTab("functionality")}
              >
                Bot Functionality
              </button>
              <button
                type="button"
                className={`tab-button ${activeTab === "info" ? "active" : ""}`}
                onClick={() => setActiveTab("info")}
              >
                Guild Information
              </button>
            </div>

            <div
              className={`tab-panel ${activeTab === "functionality" ? "active" : ""}`}
            >
              <div className="tab-section functionality-section">
                <div className="tab-section-body">
                  <ReactionRoles
                    guildId={guildId!}
                    channels={server.channels.text || []}
                    roles={server.roles || []}
                    emojis={server.emojis || []}
                  />
                </div>
              </div>

              {liveModuleEnabled && (
                <div className="tab-section functionality-section">
                  <div className="tab-section-body">
                    <LiveNotifications
                      guildId={guildId!}
                      channels={liveChannels}
                      roles={server.roles || []}
                    />
                  </div>
                </div>
              )}

              {youtubeLiveModuleEnabled && (
                <div className="tab-section functionality-section">
                  <div className="tab-section-body">
                    <YouTubeLiveNotifications
                      guildId={guildId!}
                      channels={liveChannels}
                      roles={server.roles || []}
                    />
                  </div>
                </div>
              )}

              {dropModuleEnabled && (
                <div className="tab-section functionality-section">
                  <div className="tab-section-body">
                    <DropNotifications guildId={guildId!} channels={liveChannels} />
                  </div>
                </div>
              )}

              <div className="tab-section functionality-section">
                <div className="tab-section-body">
                  <UserMonitoring
                    guildId={guildId!}
                    channels={server.channels.text || []}
                  />
                </div>
              </div>

              <div className="tab-section functionality-section">
                <div className="tab-section-body">
                  <GuildModules
                    guildId={guildId!}
                    channels={server.channels.text || []}
                    onModulesLoaded={handleModulesLoaded}
                  />
                </div>
              </div>
            </div>

            <div
              className={`tab-panel ${activeTab === "info" ? "active" : ""}`}
            >
              <div className="tab-section info-section">
                <div className="tab-section-header">
                  <div>
                    <h3>Identity & Assets</h3>
                    <p>
                      Review roles, emojis, and stickers configured in this
                      guild.
                    </p>
                  </div>
                </div>
                <div className="section-stack">
                  <CollapsibleSection
                    title="🎭 Roles"
                    count={server.roles?.length || 0}
                    defaultCollapsed
                  >
                    {!server.roles || server.roles.length === 0 ? (
                      <p className="empty-message">No roles found</p>
                    ) : (
                      <div className="role-list">
                        {server.roles.map((role) => (
                          <div
                            key={role.id}
                            className="role-item clickable"
                            onClick={() => copyToClipboard(role.id)}
                            title="Click to copy ID"
                          >
                            <div
                              className="role-color"
                              style={{
                                backgroundColor: role.color || "#99AAB5",
                              }}
                            />
                            <div className="role-details">
                              <div className="role-header">
                                <span className="role-name">{role.name}</span>
                                <span className="role-member-badge">
                                  {role.memberCount}{" "}
                                  {role.memberCount === 1
                                    ? "member"
                                    : "members"}
                                </span>
                              </div>
                              <span className="role-info">
                                Position: {role.position} • ID: {role.id}
                                {copiedId === role.id && (
                                  <span className="copied-badge">
                                    {" "}
                                    ✓ Copied!
                                  </span>
                                )}
                              </span>
                              {role.permissions.length > 0 && (
                                <span className="role-permissions">
                                  {role.permissions.join(" • ")}
                                </span>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </CollapsibleSection>

                  <CollapsibleSection
                    title="🎨 Emojis"
                    count={server.emojis?.length || 0}
                    defaultCollapsed
                  >
                    {!server.emojis || server.emojis.length === 0 ? (
                      <p className="empty-message">No custom emojis found</p>
                    ) : (
                      <>
                        <div className="emoji-grid">
                          {server.emojis.map((emoji) => (
                            <div
                              key={emoji.id}
                              className="emoji-item clickable"
                              onClick={() => copyToClipboard(emoji.id)}
                              title={`${emoji.name} - Click to copy ID`}
                            >
                              <img
                                src={emoji.url}
                                alt={emoji.name}
                                className="emoji-image"
                              />
                              <span className="emoji-name">:{emoji.name}:</span>
                              {emoji.animated && (
                                <span className="emoji-badge">GIF</span>
                              )}
                              {copiedId === emoji.id && (
                                <span className="copied-badge-overlay">✓</span>
                              )}
                            </div>
                          ))}
                        </div>
                        <p className="emoji-stats">
                          Static:{" "}
                          {server.emojis.filter((e) => !e.animated).length} •
                          Animated:{" "}
                          {server.emojis.filter((e) => e.animated).length}
                        </p>
                      </>
                    )}
                  </CollapsibleSection>

                  <CollapsibleSection
                    title="🎟️ Stickers"
                    count={server.stickers?.length || 0}
                    defaultCollapsed
                  >
                    {!server.stickers || server.stickers.length === 0 ? (
                      <p className="empty-message">No custom stickers found</p>
                    ) : (
                      <div className="sticker-grid">
                        {server.stickers.map((sticker) => {
                          const formatType =
                            sticker.format === 1
                              ? "PNG"
                              : sticker.format === 2
                                ? "APNG"
                                : sticker.format === 3
                                  ? "LOTTIE"
                                  : sticker.format === 4
                                    ? "GIF"
                                    : "Unknown";

                          return (
                            <div
                              key={sticker.id}
                              className="sticker-item clickable"
                              onClick={() => copyToClipboard(sticker.id)}
                              title={`${sticker.description || sticker.name} - Click to copy ID`}
                            >
                              {sticker.format !== 3 ? (
                                <img
                                  src={sticker.url}
                                  alt={sticker.name}
                                  className="sticker-image"
                                />
                              ) : (
                                <div className="sticker-lottie">
                                  <span>🎟️</span>
                                  <p>Lottie</p>
                                </div>
                              )}
                              <span className="sticker-name">
                                {sticker.name}
                              </span>
                              <span className="sticker-format">
                                {formatType}
                              </span>
                              {sticker.description && (
                                <span className="sticker-description">
                                  {sticker.description}
                                </span>
                              )}
                              {copiedId === sticker.id && (
                                <span className="copied-badge-overlay">✓</span>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </CollapsibleSection>
                </div>
              </div>

              <div className="tab-section info-section">
                <div className="tab-section-header">
                  <div>
                    <h3>Channel Overview</h3>
                    <p>Browse every channel category available to the bot.</p>
                  </div>
                </div>
                <div className="section-stack">
                  <CollapsibleSection
                    title="💬 Text Channels"
                    count={server.channels.text?.length || 0}
                    defaultCollapsed
                  >
                    {!server.channels.text ||
                    server.channels.text.length === 0 ? (
                      <p className="empty-message">No text channels found</p>
                    ) : (
                      <div className="channel-list">
                        {server.channels.text.map((channel) => (
                          <div
                            key={channel.id}
                            className="channel-item clickable"
                            onClick={() => copyToClipboard(channel.id)}
                            title="Click to copy ID"
                          >
                            <span className="channel-icon">
                              {channelTypeIcon(channel.type)}
                            </span>
                            <div className="channel-details">
                              <span className="channel-name">
                                {channel.name}
                              </span>
                              <span className="channel-id">
                                ID: {channel.id}
                                {copiedId === channel.id && (
                                  <span className="copied-badge">
                                    {" "}
                                    ✓ Copied!
                                  </span>
                                )}
                              </span>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </CollapsibleSection>

                  <CollapsibleSection
                    title="🔊 Voice Channels"
                    count={server.channels.voice?.length || 0}
                    defaultCollapsed
                  >
                    {!server.channels.voice ||
                    server.channels.voice.length === 0 ? (
                      <p className="empty-message">No voice channels found</p>
                    ) : (
                      <div className="channel-list">
                        {server.channels.voice.map((channel) => (
                          <div
                            key={channel.id}
                            className="channel-item clickable"
                            onClick={() => copyToClipboard(channel.id)}
                            title="Click to copy ID"
                          >
                            <span className="channel-icon">
                              {channelTypeIcon(channel.type)}
                            </span>
                            <div className="channel-details">
                              <span className="channel-name">
                                {channel.name}
                              </span>
                              <span className="channel-id">
                                ID: {channel.id}
                                {copiedId === channel.id && (
                                  <span className="copied-badge">
                                    {" "}
                                    ✓ Copied!
                                  </span>
                                )}
                              </span>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </CollapsibleSection>

                  {server.channels.announcement &&
                    server.channels.announcement.length > 0 && (
                      <CollapsibleSection
                        title="📢 Announcement Channels"
                        count={server.channels.announcement.length}
                        defaultCollapsed
                      >
                        <div className="channel-list">
                          {server.channels.announcement.map((channel) => (
                            <div
                              key={channel.id}
                              className="channel-item clickable"
                              onClick={() => copyToClipboard(channel.id)}
                              title="Click to copy ID"
                            >
                              <span className="channel-icon">
                                {channelTypeIcon(channel.type)}
                              </span>
                              <div className="channel-details">
                                <span className="channel-name">
                                  {channel.name}
                                </span>
                                <span className="channel-id">
                                  ID: {channel.id}
                                  {copiedId === channel.id && (
                                    <span className="copied-badge">
                                      {" "}
                                      ✓ Copied!
                                    </span>
                                  )}
                                </span>
                              </div>
                            </div>
                          ))}
                        </div>
                      </CollapsibleSection>
                    )}

                  {server.channels.forum &&
                    server.channels.forum.length > 0 && (
                      <CollapsibleSection
                        title="🗂️ Forum Channels"
                        count={server.channels.forum.length}
                        defaultCollapsed
                      >
                        <div className="channel-list">
                          {server.channels.forum.map((channel) => (
                            <div
                              key={channel.id}
                              className="channel-item clickable"
                              onClick={() => copyToClipboard(channel.id)}
                              title="Click to copy ID"
                            >
                              <span className="channel-icon">
                                {channelTypeIcon(channel.type)}
                              </span>
                              <div className="channel-details">
                                <span className="channel-name">
                                  {channel.name}
                                </span>
                                <span className="channel-id">
                                  ID: {channel.id}
                                  {copiedId === channel.id && (
                                    <span className="copied-badge">
                                      {" "}
                                      ✓ Copied!
                                    </span>
                                  )}
                                </span>
                              </div>
                            </div>
                          ))}
                        </div>
                      </CollapsibleSection>
                    )}

                  {server.channels.category &&
                    server.channels.category.length > 0 && (
                      <CollapsibleSection
                        title="🗄️ Categories"
                        count={server.channels.category.length}
                        defaultCollapsed
                      >
                        <div className="channel-list">
                          {server.channels.category.map((channel) => (
                            <div
                              key={channel.id}
                              className="channel-item clickable"
                              onClick={() => copyToClipboard(channel.id)}
                              title="Click to copy ID"
                            >
                              <span className="channel-icon">
                                {channelTypeIcon(channel.type)}
                              </span>
                              <div className="channel-details">
                                <span className="channel-name">
                                  {channel.name}
                                </span>
                                <span className="channel-id">
                                  ID: {channel.id}
                                  {copiedId === channel.id && (
                                    <span className="copied-badge">
                                      {" "}
                                      ✓ Copied!
                                    </span>
                                  )}
                                </span>
                              </div>
                            </div>
                          ))}
                        </div>
                      </CollapsibleSection>
                    )}

                  {server.channels.other &&
                    server.channels.other.length > 0 && (
                      <CollapsibleSection
                        title="🗃️ Other Channels"
                        count={server.channels.other.length}
                        defaultCollapsed
                      >
                        <div className="channel-list">
                          {server.channels.other.map((channel) => (
                            <div
                              key={channel.id}
                              className="channel-item clickable"
                              onClick={() => copyToClipboard(channel.id)}
                              title="Click to copy ID"
                            >
                              <span className="channel-icon">
                                {channelTypeIcon(channel.type)}
                              </span>
                              <div className="channel-details">
                                <span className="channel-name">
                                  {channel.name}
                                </span>
                                <span className="channel-id">
                                  ID: {channel.id}
                                  {copiedId === channel.id && (
                                    <span className="copied-badge">
                                      {" "}
                                      ✓ Copied!
                                    </span>
                                  )}
                                </span>
                              </div>
                            </div>
                          ))}
                        </div>
                      </CollapsibleSection>
                    )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Leave Server Confirmation Modal */}
      {showLeaveConfirm && (
        <div
          className="modal-overlay"
          onClick={() => !leaving && setShowLeaveConfirm(false)}
        >
          <div
            className="modal-container leave-confirm-modal"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="leave-modal-header">
              <div className="leave-modal-icon" aria-hidden="true">
                ⚠️
              </div>
              <div>
                <p className="leave-modal-subtitle">Critical action</p>
                <h2>Remove ApocalypseBot from this server?</h2>
                <p className="leave-modal-description">
                  The bot will immediately leave <strong>{server.name}</strong>{" "}
                  and stop responding to members in this guild.
                </p>
              </div>
            </div>

            <div className="leave-modal-summary">
              <div>
                <span className="summary-label">Server</span>
                <span className="summary-value">{server.name}</span>
              </div>
              <div>
                <span className="summary-label">Members</span>
                <span className="summary-value">
                  {server.memberCount.toLocaleString()}
                </span>
              </div>
              <div>
                <span className="summary-label">Server ID</span>
                <span className="summary-value summary-code">{server.id}</span>
              </div>
            </div>

            <div className="leave-modal-body">
              <h3>What will happen</h3>
              <ul className="impact-list">
                <li>
                  <span className="impact-title">Bot removed immediately</span>
                  <span className="impact-description">
                    ApocalypseBot leaves the guild and all commands are
                    disabled.
                  </span>
                </li>
                <li>
                  <span className="impact-title">Reaction roles reset</span>
                  <span className="impact-description">
                    All reaction role messages and mappings will be deleted.
                  </span>
                </li>
                <li>
                  <span className="impact-title">Monitoring stops</span>
                  <span className="impact-description">
                    User and message monitoring modules stop tracking activity.
                  </span>
                </li>
              </ul>

              <div className="leave-modal-warning">
                <strong>Need to come back later?</strong>
                <p>
                  You can re-invite the bot at any time, but every automation
                  will need to be set up again.
                </p>
              </div>
            </div>

            <div className="leave-modal-actions">
              <button
                type="button"
                className="btn btn-secondary"
                onClick={() => setShowLeaveConfirm(false)}
                disabled={leaving}
              >
                Keep Bot
              </button>
              <button
                type="button"
                className="btn btn-danger"
                onClick={handleLeaveServer}
                disabled={leaving}
              >
                {leaving ? "Removing..." : "Confirm Leave"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default ServerDetails;
